'use client';
import type React from 'react';

type SessionMode = 'idle' | 'connecting' | 'listening' | 'speaking' | 'ended';

interface VoiceRingFallbackProps {
  size?: number;
  onClick?: () => void;
  className?: string;
  isActive?: boolean;
  sessionMode?: SessionMode;
  interactive?: boolean;
}

function getFallbackState(sessionMode: SessionMode, isActive: boolean) {
  if (sessionMode === 'connecting') {
    return {
      label: 'Connecting',
      labelColor: '#f59e0b',
      micColor: '#fbbf24',
      shadow: '0 0 10px rgba(245,158,11,0.5)',
      blob: 'radial-gradient(circle at 34% 24%, rgba(251,191,36,0.95), rgba(245,158,11,0.34) 18%, transparent 34%), radial-gradient(circle at 72% 70%, rgba(217,119,6,0.78), transparent 43%), conic-gradient(from 225deg, #f59e0b 0deg, #06b6d4 105deg, #1d4ed8 190deg, #0f172a 252deg, #fbbf24 360deg)',
    };
  }
  if (sessionMode === 'speaking') {
    return {
      label: 'Talking',
      labelColor: '#34d399',
      micColor: '#a7f3d0',
      shadow: '0 0 10px rgba(52,211,153,0.5)',
      blob: 'radial-gradient(circle at 34% 24%, rgba(167,243,208,0.95), rgba(52,211,153,0.34) 18%, transparent 34%), radial-gradient(circle at 72% 70%, rgba(37,99,235,0.78), transparent 43%), conic-gradient(from 225deg, #10b981 0deg, #06b6d4 105deg, #1d4ed8 190deg, #0f172a 252deg, #34d399 360deg)',
    };
  }
  if (sessionMode === 'listening' || isActive) {
    return {
      label: 'Listening',
      labelColor: '#22d3ee',
      micColor: '#cffafe',
      shadow: '0 0 10px rgba(34,211,238,0.5)',
      blob: 'radial-gradient(circle at 34% 24%, rgba(207,250,254,0.95), rgba(34,211,238,0.34) 18%, transparent 34%), radial-gradient(circle at 72% 70%, rgba(37,99,235,0.78), transparent 43%), conic-gradient(from 225deg, #14b8a6 0deg, #06b6d4 105deg, #1d4ed8 190deg, #0f172a 252deg, #22d3ee 360deg)',
    };
  }
  return {
    label: 'Tap to Talk',
    labelColor: 'white',
    micColor: 'white',
    shadow: '0 0 8px rgba(255,255,255,0.3)',
    blob: 'radial-gradient(circle at 34% 24%, rgba(125,250,255,0.95), rgba(34,211,238,0.35) 18%, transparent 34%), radial-gradient(circle at 72% 70%, rgba(37,99,235,0.85), transparent 43%), conic-gradient(from 225deg, #14b8a6 0deg, #0891b2 92deg, #1d4ed8 178deg, #0f172a 245deg, #10b981 360deg)',
  };
}

export default function VoiceRingFallback({
  size = 200,
  onClick,
  className = '',
  isActive = false,
  sessionMode = 'idle',
  interactive = true,
}: VoiceRingFallbackProps) {
  const ringState = getFallbackState(sessionMode, isActive);
  const buttonSize = Math.max(76, Math.min(96, size * 0.42));
  const blobInset = Math.max(8, size * 0.14);
  const coreInset = Math.max(18, size * 0.27);

  return (
    <div
      className={`relative inline-flex items-center justify-center group ${interactive ? 'cursor-pointer' : ''} ${className}`}
      onClick={interactive ? onClick : undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? ringState.label : undefined}
      aria-hidden={interactive ? undefined : true}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (!interactive) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
      style={{ width: size, height: size, outline: 'none', WebkitTapHighlightColor: 'transparent' }}
    >
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: blobInset,
          borderRadius: '43% 57% 48% 52% / 53% 44% 56% 47%',
          background: ringState.blob,
          boxShadow: '0 24px 56px rgba(0, 0, 0, 0.48), 0 0 42px rgba(34, 211, 238, 0.22), inset 0 0 18px rgba(255, 255, 255, 0.08)',
          opacity: 0.95,
          pointerEvents: 'none',
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: coreInset,
          borderRadius: 999,
          background: 'radial-gradient(circle at 50% 35%, rgba(20, 24, 42, 0.98), #070814 72%, #04050a 100%)',
          boxShadow: 'inset 0 0 0 1px rgba(125, 250, 255, 0.08), inset 0 -18px 36px rgba(0, 0, 0, 0.28), 0 0 26px rgba(5, 8, 20, 0.58)',
          pointerEvents: 'none',
        }}
      />
      <div
        className="flex flex-col items-center justify-center transition-transform duration-300 group-hover:scale-105"
        style={{
          position: 'relative',
          zIndex: 1,
          width: buttonSize,
          height: buttonSize,
          borderRadius: 999,
          background: 'transparent',
          textAlign: 'center',
        }}
      >
        <svg
          viewBox="0 0 24 24"
          fill={ringState.micColor}
          style={{
            width: Math.max(16, size * 0.085),
            height: Math.max(16, size * 0.085),
            filter: `drop-shadow(${ringState.shadow})`,
            transition: 'fill 0.3s ease, filter 0.3s ease',
          }}
        >
          <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
          <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
        </svg>
        <span className="font-semibold tracking-wide" style={{
          fontSize: Math.max(11, size * 0.055),
          marginTop: Math.max(4, size * 0.01),
          color: ringState.labelColor,
          textShadow: ringState.shadow,
          transition: 'color 0.3s ease, text-shadow 0.3s ease',
          textAlign: 'center',
          whiteSpace: 'nowrap',
          display: 'block',
          width: '100%',
        }}>
          {ringState.label}
        </span>
      </div>
    </div>
  );
}
